import styled, { css, AnyStyledComponent } from 'styled-components'

import { CarouselProvider, Slider, ButtonBack, ButtonNext } from 'pure-react-carousel'
import { desktopBreakpoint, mobileBreakpoint, tabletBreakpoint, wideScreenBreakpoint } from 'react-components'
import ExternalLink from '../external-link'
import FullStar from '../../../assets/icons/star.svg'
import Offer from '../../../assets/icons/offer-badge.svg'
import { getBasePath } from '../../../utils/miscellaneous-utils'
import FreeCancellation from '../../../assets/icons/free-cancellation-shield.svg'
import { GreenTickIcon } from '../../common/icons'

export const GridCardContainer = styled.div<{ $isObjectOverview?: boolean }>(
    ({ theme, $isObjectOverview }) => css`
        display: flex;
        flex-direction: column;
        height: 100%;
        cursor: pointer;

        background-color: ${theme.colors.white};
        box-shadow: 0px 1px 5px 0px #00000014, 0px 2px 1px 0px #0000000a, 0px 1px 2px 0px #0000000f;
        border-radius: 1rem;

        ${$isObjectOverview &&
        css`
            margin-bottom: ${theme.spacing.medium};
        `}
    `,
)

export const FeatureIconsContainer = styled.div`
    margin-top: ${({ theme }) => theme.spacing.xxSmall};
    margin-bottom: ${({ theme }) => theme.spacing.xxSmall};
    width: 100%;
    overflow: hidden;
`

export const CTAButton = styled.button`
    width: 100%;
    padding: 12px 16px;
    border-radius: 12px;
    font-family: ${({ theme }) => theme.typography.font.family.lato};
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1rem;
    cursor: pointer;
    text-align: center;
    border: 1px solid transparent;

    background-color: ${({ theme }) => theme.colors.tfwYellow};
    color: ${({ theme }) => theme.colors.tfwBlack99};

    &:hover {
        background-color: ${({ theme }) => theme.colors.tfwYellowHover};
    }

    &:active {
        transform: translateY(1px);
    }

    /*
    In grid view on desktop, the button breaks due to insufficient width.
    A proper fix will be implemented in the future.
    */
    @media only screen and (min-width: ${desktopBreakpoint.min}px) and (max-width: ${wideScreenBreakpoint.min}px) {
        font-size: ${({ theme }) => theme.typography.font.size.xSmall};
        padding: ${({ theme }) => theme.spacing.small};
    }
`

export const BottomRow = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: auto;
`

export const PriceWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 0.5rem;
    gap: 0.25rem;
`

export const ButtonWrapper = styled.div`
    max-width: 140px;
    flex-shrink: 0;
    display: flex;
    align-items: flex-end;
    @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
        max-width: 90px;
    }
`

export const ImageContainer = styled.div(
    ({ theme }) => css`
        position: relative;
        overflow: hidden;
        border-radius: 1rem 1rem 0 0;
    `,
)

export const NextImageContainer = styled.div`
    height: 100%;
    width: 100%;
`

export const CardContent = styled.div<{ $hasBottomTags?: boolean }>(
    ({ theme, $hasBottomTags }) => css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex: 1;
        padding: 0.75rem 1rem;
        border-radius: 1rem;

        ${$hasBottomTags &&
        css`
            background: linear-gradient(190.38deg, #ffffff 72.45%, #eee3ff 100%);
        `}
    `,
)

export const CardTopSection = styled.div``

export const BookingHighlights = styled.div`
    display: flex;
    margin-top: 0.5rem;
    gap: 0.25rem 1rem;
    flex-wrap: wrap;
`

export const TooltipContainer = styled.div`
    position: absolute;
    bottom: 150%;
    left: 70%;
    transform: translateX(-50%);
    background-color: #333;
    color: white;
    padding: 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    white-space: normal;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s, visibility 0.2s;
    z-index: 10;
    width: max-content;
    max-width: 200px;
    text-align: center;

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: #333 transparent transparent transparent;
    }
`

export const DTVContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: pointer;

    &:hover ${TooltipContainer} {
        opacity: 1;
        visibility: visible;
    }
`

export const DTVStarContainer = styled.div`
    display: flex;
`

export const DTVRatingText = styled.span`
    display: flex;
    flex-shrink: 0;
    font-family: ${({ theme }) => theme.typography.font.family.lato};
    font-size: 0.75rem;
    line-height: 1rem;
    color: #282828;
`

export const DTVImage = styled.img`
    width: 2rem;
    height: 0.6875rem;
    object-fit: contain;
    margin-left: 0.25rem;
`

export const DTVLabel = styled.span`
    font-family: ${({ theme }) => theme.typography.font.family.lato};
    font-size: 0.75rem;
    line-height: 1rem;
    color: #6f6f6f;
    margin-left: 0.25rem;
`

export const QuickResponderTag = styled.div`
    display: inline-flex;
    align-items: center;
    color: #0d7c4f;
    font-size: 0.75rem;
    line-height: 1rem;
`

export const StyledFastResponderIcon = styled(GreenTickIcon)`
    width: 0.75rem;
    height: 0.75rem;
    margin-right: 0.5rem;
    vertical-align: middle;
`

export const CardBottomContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: flex-end;
    gap: 0.5rem;
`

export const BottomContainerWrapper = styled.div`
    width: 100%;
`

export const CtaPriceContainer = styled.div`
    display: flex;
`

export const OfferWrapper = styled.div``

export const PopularChoiceTagContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
`

export const ImageSkeleton = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #f0f0f0;
    z-index: 1;
`

export const PopularChoiceTagText = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: 0.75rem;
        line-height: 1rem;
        font-weight: 600;
        color: #8f6408;
    `,
)

export const BottomTraumTagContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.75rem;
`

export const BottomTraumTagImage = styled.img`
    width: 1.5rem;
    height: 1.5rem;
    object-fit: contain;
`

export const BottomTraumTagText = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: 0.875rem;
        line-height: 1.375rem;
        font-weight: 600;
        color: #8f6408;
    `,
)

export const LocationRatingContainer = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
    `,
)

export const LocationText = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: 0.75rem;
        line-height: 1rem;
        color: #6c6c6c;
        font-weight: 400;
    `,
)

export const LocationContainer = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        gap: 0.25rem;
    `,
)

export const FullStarIcon = styled(FullStar as AnyStyledComponent)`
    margin-right: 0.312rem;
`

export const RatingText = styled.span(
    ({ theme }) => css`
        color: #282828;
    `,
)

export const RatingCount = styled.span(
    ({ theme }) => css`
        margin-left: 0.25rem;
        color: #6c6c6c;
    `,
)

export const PropertyTitle = styled.h3(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-weight: 600;
        font-size: 1rem;
        line-height: 1.5rem;
        color: #282828;
        margin-top: 2px;
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    `,
)

export const SeoHref = styled(ExternalLink)`
    display: flex;
    font: inherit;
    text-decoration: none;
`

export const PropertyTitleLink = styled(ExternalLink)`
    display: inline;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font: inherit;
    text-decoration: none;
`

export const PropertyDescription = styled.p(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: 0.875rem;
        color: #282828;
        margin-top: 2px;
        line-height: 1.375rem;
        display: flex;
        overflow: hidden;
        text-overflow: ellipsis;
    `,
)

export const PriceContainer = styled.div(
    ({ theme }) => css`
        display: flex;
        font-family: ${theme.typography.font.family.lato};
        font-weight: 600;
        font-size: 1rem;
        line-height: 1.5rem;
        margin-top: 0.25rem;
    `,
)

export const SlasherPrice = styled.div(
    ({ theme }) => css`
        display: flex;
        position: relative;
        font-weight: 500;
        color: #aaaaaa;
        margin-right: 4px;
        text-decoration: line-through;

        @media only screen and (min-width: ${desktopBreakpoint.min}px) and (max-width: ${wideScreenBreakpoint.min}px) {
            font-size: 1rem;
            line-height: 1.5rem;
        }
    `,
)

export const PersonDetails = styled.div(
    ({ theme }) => css`
        display: flex;
        font-family: ${theme.typography.font.family.lato};
        font-weight: 400;
        font-size: 0.75rem;
        line-height: 1rem;
        color: #6c6c6c;
    `,
)

const fadeIn = css`
    @keyframes fadeIn {
        0% {
            opacity: 0;
            transform: translateY(-6px);
        }
        100% {
            opacity: 1;
            transform: translateY(0);
        }
    }
`

export const TopTraumTagPill = styled.div<{ $showCheckbox?: boolean }>(
    ({ theme, $showCheckbox }) => css`
        ${fadeIn}
        display: flex;
        align-items: center;
        gap: 0.25rem;
        position: absolute;
        top: 0.75rem;
        left: ${$showCheckbox ? '3rem' : '0.5rem'};
        padding: 0.25rem 0.5rem;
        border-radius: 0.5rem;
        color: ${theme.colors.tfwPurple};
        background: rgba(255, 255, 255, 0.9);
        backdrop-filter: blur(4px);
        font-size: 0.75rem;
        font-weight: 500;
        animation: fadeIn 0.8s ease-out;
    `,
)

export const TraumTagIconStyled = styled.svg`
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
`

export const TraumTagLabel = styled.span`
    font-size: 0.75rem;
    line-height: 1rem;
    color: #282828;
    white-space: nowrap;
`

export const VirtualTourWrapper = styled.div<{ shouldShowRating: boolean }>(
    ({ shouldShowRating }) => css`
        position: absolute;
        bottom: ${shouldShowRating ? '2rem' : '0.5rem'};
        left: 0.5rem;
    `,
)

export const ObjectTopRecommendationPill = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 0rem;
        right: 0rem;
        width: 4.5rem;
        height: 3.5rem;
        background: radial-gradient(100% 100% at 100% 100%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
        justify-content: center;
    `,
)

export const TopRecommendationWrapper = styled.div(
    ({ theme }) => css`
        display: flex;
        font-family: ${theme.typography.font.family.lato};
        font-weight: 600;
        font-size: 0.625rem;
        line-height: 0.75rem;
        color: #ffffff;
        position: absolute;
        bottom: 0.5rem;
        right: 0.5rem;
    `,
)

export const FavoriteButton = styled.div(
    ({ theme }) => css`
        position: absolute;
        top: 0;
        right: 0;
        width: 3.5rem;
        height: 3.5rem;
        background: radial-gradient(100% 100% at 100% 0%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
        border-radius: 0.75rem;
        display: flex;
        align-items: center;
        justify-content: center;
    `,
)

export const WishlistIconContainer = styled.div`
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
`

export const PartnerLogoContainer = styled.div<{ $partnerName: string }>(
    ({ theme, $partnerName }) => css`
        position: absolute;
        top: ${theme.spacing.small};
        right: ${theme.spacing.small};
        background-color: #fff;
        border-radius: ${$partnerName === 'holidu' ? '0.5rem' : '0.25rem'};
        padding: ${$partnerName === 'holidu' ? '0.25rem' : '0.25rem 0.5rem'};
        display: flex;
        align-items: center;
        justify-content: center;
    `,
)

export const PartnerLogo = styled.img<{ $partnerName: string }>(
    ({ theme, $partnerName }) => css`
        width: ${$partnerName === 'holidu' ? '4.5rem' : '3rem'};
        height: auto;
        object-fit: contain;
    `,
)

export const OfferBadge = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        width: fit-content;
        min-width: 8.4375rem;
        background: linear-gradient(90deg, #d2faea 0%, #ffffff 100%);
        padding: 0.25rem 0.5rem;
        border-radius: 12px;
        margin-top: 0.5rem;
    `,
)

export const RatingBadge = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 0;
        left: 0.8rem;
        min-width: 5.5rem;
        background-color: white;
        padding: 0.25rem 0.5rem 0;
        font: 400 0.875rem / 1.375rem ${theme.typography.font.family.lato};
        border-radius: 0.6rem 0.6rem 0 0;
        justify-content: center;

        /* Inverse radius on left side (slope up) */
        &::before {
            content: '';
            position: absolute;
            bottom: 0;
            left: -0.75rem;
            width: 0.8rem;
            height: 0.75rem;
            background: radial-gradient(circle at 0 0, transparent 0.75rem, white 0.75rem);
        }

        /* Inverse radius on right side (slope up) */
        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            right: -0.75rem;
            width: 0.8rem;
            height: 0.75rem;
            background: radial-gradient(circle at 100% 0, transparent 0.75rem, white 0.75rem);
        }
    `,
)

export const OfferIcon = styled(Offer as AnyStyledComponent)`
    width: 1rem;
    height: 1rem;
    margin-right: 0.25rem;
`

export const OfferText = styled.span`
    color: #0d7c4f;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 400;
`

export const CheckBoxLabel = styled.label`
    padding: 0.5rem;
    display: ${props => props.theme.style.display.inlineBlock};
    position: ${props => props.theme.style.position.relative};
    cursor: pointer;
    width: 100%;
    height: 100%;
    outline: 0;
    background-color: white;
    border-radius: 0.3rem;
`

const checkBoxUrl = `url(${getBasePath('/assets/icons/white-check.svg')})`

export const InputCheckbox = styled.input`
    &:checked + ${CheckBoxLabel} {
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        background: ${props => props.theme.colors.tfwBlack99} ${checkBoxUrl} no-repeat center;
        background-size: 1rem;
    }

    display: none;
`

export const CheckBox = styled.div`
    width: 1.5rem;
    height: 1.5rem;
`

export const InformationWrapper = styled.div`
    display: ${props => props.theme.style.display.flex};
    position: absolute;
    top: 0.75rem;
    left: 0.75rem;
    z-index: 20;
    width: 1.5rem;
    height: 1.5rem;
`

export const FreeCancellationContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.5rem;
`

export const FreeCancellationIcon = styled(FreeCancellation as AnyStyledComponent)`
    width: 1rem;
    height: 1rem;
`

export const FreeCancellationText = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: 0.75rem;
        font-weight: 400;
        line-height: 1rem;
        color: #282828;
    `,
)

export const StyledCarouselProvider = styled(CarouselProvider)`
    position: relative;
    width: 100%;
    /* height: 100%; Intrinsic height handled by provider if configured, or container expands */
    border-radius: 1rem;
    overflow: hidden;

    @media only screen and (max-width: ${mobileBreakpoint.max}px) {
        border-radius: 0.5rem;
    }
`

export const StyledSlider = styled(Slider)`
    width: 100%;
    height: 100%;
    position: relative;
`

export const CarouselIcon = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`

const navigationButtonStyles = css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.white};
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 2;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.16);
    opacity: 1;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        display: none;
    }

    /* Hide on mobile */
    @media only screen and (max-width: ${mobileBreakpoint.max}px) {
        display: none !important;
    }
`

export const StyledButtonBack = styled(ButtonBack)`
    ${navigationButtonStyles}
    left: 0.5rem;
`

export const StyledButtonNext = styled(ButtonNext)`
    ${navigationButtonStyles}
    right: 0.5rem;
`
